import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import { useNavigate } from '@tanstack/react-router'
import { Card, CardContent, Button, Tabs, TabsList, TabsTrigger, TabsContent } from '@duck/ui'
import { useTar } from '@/modules'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { apis } from '@/modules/apiClients'
import { StandardLoader } from '../StandardLoader'
import { NoDataEmptyState } from '../EmptyStates'
import { TaskDataInput } from '../DataInputs/TaskDataInput'
import { KpiStatCard } from './KpiStatCard'

type ChallengesBucketMode = 'daily' | 'weekly'
type SignupsRange = 'last7' | 'all'
type ChallengesRange = 'last7' | 'all'
type TasksRange = 'top5' | 'all'

// ── Chart chrome ────────────────────────────────────────────────────────────
// The page registers no echarts theme, so each chart carries its own on-brand
// palette and neutral axes: emerald for the primary series, a muted steel for
// the companion series, and faint grid lines / axis text sized to stay legible
// on both the light and dark card surfaces.
// NOTE: comma-separated HSL, not the space-separated CSS Color 4 form — echarts'
// zrender parser can't read `hsl(h s% l%)`, so it fails to derive the hover
// (emphasis) colour and renders the hovered bar transparent (it disappears).
const CHART_GREEN = 'hsl(160, 70%, 45%)'
const CHART_STEEL = 'hsl(214, 22%, 60%)'
const CHART_AXIS_TEXT = 'hsl(220, 6%, 58%)'
const CHART_GRID_LINE = 'rgba(148, 163, 184, 0.14)'
const CHART_AXIS_LINE = 'rgba(148, 163, 184, 0.22)'

export const AdminStatistics: React.FC = () => {
  const { t } = useTar()
  const navigate = useNavigate()
  useDocumentTitle('KPIs', 'admin')

  const [data, setData] = useState<import('@duck/api-definitions').UserService.AdminStatisticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [challengesMode, setChallengesMode] = useState<ChallengesBucketMode>('daily')
  const [signupsRange, setSignupsRange] = useState<SignupsRange>('last7')
  const [challengesRange, setChallengesRange] = useState<ChallengesRange>('last7')
  const [tasksRange, setTasksRange] = useState<TasksRange>('top5')
  // Empty = all tasks (default). singlePick means at most one id when narrowed.
  const [gradeDistributionTaskIds, setGradeDistributionTaskIds] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState('users')

  const signupsRef = useRef<HTMLDivElement | null>(null)
  const conversionFunnelRef = useRef<HTMLDivElement | null>(null)
  const challengesRef = useRef<HTMLDivElement | null>(null)
  const tasksCountRef = useRef<HTMLDivElement | null>(null)
  const tasksGradeRef = useRef<HTMLDivElement | null>(null)
  const gradeDistributionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await apis.userService.adminStatisticsGet()
        if (!cancelled) setData(response.data)
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching admin statistics:', err)
          setError(err instanceof Error ? err.message : 'Failed to fetch statistics')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchData()

    return () => { cancelled = true }
  }, [])

  // All tab panels stay mounted (forceMount) and are hidden via CSS when
  // inactive, so their charts initialize at 0x0. When the active tab changes,
  // the newly-visible chart's container finally has a real size — nudge every
  // chart to re-measure (each already listens for window 'resize'). rAF lets
  // the browser apply the show/hide layout before we measure.
  useEffect(() => {
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    return () => cancelAnimationFrame(id)
  }, [activeTab])

  const formatPct = (rate: number) => `${Math.round(rate * 100)}%`

  // Share of a whole for a tile's proportion gauge; guards divide-by-zero.
  const share = (part: number, whole: number) => (whole > 0 ? part / whole : 0)

  const formatDateLabel = (isoString: string) => {
    const date = new Date(isoString)
    if (Number.isNaN(date.getTime())) {
      return isoString
    }
    return date.toLocaleDateString()
  }

  const formatWeekLabel = (isoString: string) => {
    const date = new Date(isoString)
    if (Number.isNaN(date.getTime())) {
      return isoString
    }
    return t('admin.statisticsWeekOfDATE', { date: date.toLocaleDateString() })
  }

  // User signups over time (single-series bar chart)
  useEffect(() => {
    if (!signupsRef.current || !data || data.userSignupsOverTime.length === 0) {
      return
    }

    let signups = data.userSignupsOverTime
    if (signupsRange === 'last7' && signups.length > 7) {
      signups = signups.slice(-7)
    }

    const instance = echarts.init(signupsRef.current)

    const labels = signups.map(point => formatDateLabel(point.date))
    const counts = signups.map(point => point.count ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          color: CHART_AXIS_TEXT,
          rotate: 45
        },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          name: t('admin.statisticsUserSignupsSeries'),
          type: 'bar',
          data: counts,
          barMaxWidth: 34,
          itemStyle: { color: CHART_GREEN, borderRadius: [4, 4, 0, 0] }
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [data, signupsRange, t])

  // Conversion funnel (descending column chart): candidates -> submitted an
  // assignment -> joined Gamma -> started a 2nd challenge -> started a 3rd.
  useEffect(() => {
    if (!conversionFunnelRef.current || !data) {
      return
    }

    const { conversionStats: cs } = data
    const stages = [
      { label: t('adminKpis.conversions.funnelAllCandidates'), value: cs.candidatesTotal },
      { label: t('adminKpis.conversions.funnelSubmitted'), value: cs.converted + cs.notConvertedFinished },
      { label: t('adminKpis.conversions.funnelGamma'), value: cs.converted },
      { label: t('adminKpis.conversions.funnelSecondChallenge'), value: cs.startedSecondChallenge },
      { label: t('adminKpis.conversions.funnelThirdChallenge'), value: cs.startedThirdChallenge },
    ]

    const instance = echarts.init(conversionFunnelRef.current)

    // Deepening brand-green ramp: the widest stage (all candidates) reads
    // lightest, each surviving cohort a shade deeper — colour reinforces the
    // narrowing. Kept dark enough for white in-segment labels to stay legible
    // in both themes (contrast is against the segment, not the card).
    const funnelColors = [
      'hsl(160, 60%, 34%)',
      'hsl(160, 64%, 31%)',
      'hsl(160, 68%, 28%)',
      'hsl(160, 72%, 25%)',
      'hsl(160, 78%, 22%)',
    ]

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        // Counts, not percentages — echarts' default funnel tooltip appends '%'.
        formatter: '{b}: {c}',
      },
      series: [
        {
          name: t('adminKpis.conversions.funnelTitle'),
          type: 'funnel',
          left: '8%',
          right: '8%',
          top: 12,
          bottom: 12,
          // Keep the stage order fixed instead of re-sorting by value, so the
          // funnel always reads all candidates -> submitted -> Gamma -> 2nd -> 3rd.
          sort: 'none',
          // Floor the narrowest band so even small/zero cohorts stay wide enough
          // to host their label; width still tapers to encode the drop-off.
          minSize: '24%',
          maxSize: '100%',
          gap: 3,
          funnelAlign: 'center',
          itemStyle: {
            borderWidth: 0,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{b}\n{c}',
            color: '#ffffff',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 16,
            textShadowColor: 'rgba(0, 0, 0, 0.35)',
            textShadowBlur: 3,
          },
          // Enlarging the inside label on hover changes the segment's bounding
          // box, so echarts flip-flops highlight/downplay as the cursor moves —
          // the blink. Disable the hover restyle; the tooltip is the affordance.
          emphasis: {
            disabled: true,
          },
          data: stages.map((stage, i) => ({
            value: stage.value,
            name: stage.label,
            itemStyle: { color: funnelColors[i] },
          })),
        },
      ],
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [data, t])

  // Challenges over time (two-series bar chart, daily/weekly)
  useEffect(() => {
    if (!challengesRef.current || !data) {
      return
    }

    let buckets =
      challengesMode === 'daily'
        ? data.challengesOverTimeDaily
        : data.challengesOverTimeWeekly

    if (!buckets || buckets.length === 0) {
      return
    }

    if (challengesMode === 'daily' && challengesRange === 'last7' && buckets.length > 7) {
      buckets = buckets.slice(-7)
    }

    const instance = echarts.init(challengesRef.current)

    const labels =
      challengesMode === 'daily'
        ? buckets.map(point => formatDateLabel(point.bucketStart))
        : buckets.map(point => formatWeekLabel(point.bucketStart))

    const startedSeries = buckets.map(point => point.startedCount ?? 0)
    const submittedSeries = buckets.map(point => point.submittedCount ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        top: 0,
        icon: 'roundRect',
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { color: CHART_AXIS_TEXT }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: {
          color: CHART_AXIS_TEXT,
          rotate: 45
        },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          name: t('common.started'),
          type: 'bar',
          stack: 'challenges',
          data: startedSeries,
          barMaxWidth: 34,
          itemStyle: { color: CHART_STEEL, borderRadius: [4, 4, 0, 0] }
        },
        {
          name: t('common.submitted'),
          type: 'bar',
          stack: 'challenges',
          data: submittedSeries,
          barMaxWidth: 34,
          itemStyle: { color: CHART_GREEN, borderRadius: [4, 4, 0, 0] }
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [data, challengesMode, challengesRange, t])

  // Tasks overview - challenges count (horizontal bar, sorted by count desc)
  useEffect(() => {
    if (!tasksCountRef.current || !data || data.tasksOverview.length === 0) {
      return
    }

    const sortedByCount = [...data.tasksOverview]
      .sort((a, b) => (a.challengesCount ?? 0) - (b.challengesCount ?? 0))
      .slice(tasksRange === 'top5' ? -5 : undefined)

    const instance = echarts.init(tasksCountRef.current)

    const labels = sortedByCount.map(entry => entry.taskName || entry.taskId)
    const challengesCounts = sortedByCount.map(entry => entry.challengesCount ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: t('admin.statisticsTasksChallengesYAxis'),
        nameTextStyle: { color: CHART_AXIS_TEXT },
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: CHART_AXIS_TEXT },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false }
      },
      series: [
        {
          name: t('admin.statisticsTasksChallengesSeries'),
          type: 'bar',
          data: challengesCounts,
          barMaxWidth: 22,
          itemStyle: { color: CHART_GREEN, borderRadius: [0, 4, 4, 0] }
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [data, tasksRange, t])

  // Tasks overview - average grade (horizontal bar, sorted by grade desc)
  useEffect(() => {
    if (!tasksGradeRef.current || !data || data.tasksOverview.length === 0) {
      return
    }

    const sortedByGrade = [...data.tasksOverview]
      .sort((a, b) => (a.averageGrade ?? 0) - (b.averageGrade ?? 0))
      .slice(tasksRange === 'top5' ? -5 : undefined)

    const instance = echarts.init(tasksGradeRef.current)

    const labels = sortedByGrade.map(entry => entry.taskName || entry.taskId)
    const averageGrades = sortedByGrade.map(entry => entry.averageGrade ?? 0)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: t('admin.statisticsTasksAverageGradeYAxis'),
        min: 1,
        max: 10,
        nameTextStyle: { color: CHART_AXIS_TEXT },
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: CHART_AXIS_TEXT },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false }
      },
      series: [
        {
          name: t('admin.statisticsTasksAverageGradeSeries'),
          type: 'bar',
          data: averageGrades,
          barMaxWidth: 22,
          itemStyle: { color: CHART_GREEN, borderRadius: [0, 4, 4, 0] }
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [data, tasksRange, t])

  // Grade distribution (1-10 histogram) for all tasks or a single selected task
  useEffect(() => {
    if (!gradeDistributionRef.current || !data || data.tasksOverview.length === 0) {
      return
    }

    const selectedTaskId = gradeDistributionTaskIds[0]
    const distribution = new Array<number>(10).fill(0)
    for (const entry of data.tasksOverview) {
      if (selectedTaskId && entry.taskId !== selectedTaskId) continue
      const entryDistribution = entry.gradeDistribution ?? []
      for (let i = 0; i < distribution.length; i++) {
        distribution[i] += entryDistribution[i] ?? 0
      }
    }

    const instance = echarts.init(gradeDistributionRef.current)

    const labels = Array.from({ length: 10 }, (_, i) => String(i + 1))

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        name: t('admin.statisticsGradeDistributionXAxis'),
        nameTextStyle: { color: CHART_AXIS_TEXT },
        axisLabel: { color: CHART_AXIS_TEXT },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: t('admin.statisticsGradeDistributionYAxis'),
        minInterval: 1,
        nameTextStyle: { color: CHART_AXIS_TEXT },
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          name: t('admin.statisticsGradeDistributionSeries'),
          type: 'bar',
          data: distribution,
          barMaxWidth: 40,
          itemStyle: { color: CHART_GREEN, borderRadius: [4, 4, 0, 0] }
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [data, gradeDistributionTaskIds, t])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <StandardLoader size="md" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{t('common.statistics')}</h2>
        <p className="text-sm text-destructive">{t('common.error')}: {error}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{t('common.statistics')}</h2>
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              title={t('admin.statisticsNoDataTitle')}
              description={t('admin.statisticsNoDataDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  const hasAnyData =
    data.userSignupsOverTime.length > 0 ||
    data.challengesOverTimeDaily.length > 0 ||
    data.challengesOverTimeWeekly.length > 0 ||
    data.tasksOverview.length > 0

  if (!hasAnyData) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{t('common.statistics')}</h2>
        <Card>
          <CardContent className="pt-6">
            <NoDataEmptyState
              title={t('admin.statisticsNoDataTitle')}
              description={t('admin.statisticsNoDataDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
          {t('adminKpis.title')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('adminKpis.description')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full">
          <TabsTrigger value="users" className="flex-1">{t('adminKpis.tabs.users')}</TabsTrigger>
          <TabsTrigger value="conversions" className="flex-1">{t('adminKpis.tabs.conversions')}</TabsTrigger>
          <TabsTrigger value="engagement" className="flex-1">{t('adminKpis.tabs.engagement')}</TabsTrigger>
          <TabsTrigger value="tasks" className="flex-1">{t('adminKpis.tabs.tasks')}</TabsTrigger>
        </TabsList>

        <TabsContent value="users" forceMount className="mt-4 space-y-6 data-[state=inactive]:hidden">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <KpiStatCard emphasis label={t('adminKpis.users.total')} value={data.userSegments.totalUsers} tooltip={t('adminKpis.users.totalTooltip')} fraction={1} />
            <KpiStatCard label={t('adminKpis.users.regular')} value={data.userSegments.regular} tooltip={t('adminKpis.users.regularTooltip')} fraction={share(data.userSegments.regular, data.userSegments.totalUsers)} />
            <KpiStatCard label={t('adminKpis.users.candidates')} value={data.userSegments.candidates} tooltip={t('adminKpis.users.candidatesTooltip')} fraction={share(data.userSegments.candidates, data.userSegments.totalUsers)} />
            <KpiStatCard label={t('adminKpis.users.students')} value={data.userSegments.students} tooltip={t('adminKpis.users.studentsTooltip')} fraction={share(data.userSegments.students, data.userSegments.totalUsers)} />
            <KpiStatCard label={t('adminKpis.users.partnerAdmins')} value={data.userSegments.partnerAdmins} tooltip={t('adminKpis.users.partnerAdminsTooltip')} fraction={share(data.userSegments.partnerAdmins, data.userSegments.totalUsers)} />
            <KpiStatCard label={t('adminKpis.users.admins')} value={data.userSegments.admins} tooltip={t('adminKpis.users.adminsTooltip')} fraction={share(data.userSegments.admins, data.userSegments.totalUsers)} />
            <KpiStatCard
              label={t('adminKpis.users.verified')}
              value={data.userSegments.verified}
              tooltip={t('adminKpis.users.verifiedTooltip')}
              fraction={share(data.userSegments.verified, data.userSegments.totalUsers)}
              sublabel={
                data.userSegments.totalUsers > 0
                  ? `${formatPct(data.userSegments.verified / data.userSegments.totalUsers)} ${t('adminKpis.users.verifiedSublabel')}`
                  : undefined
              }
            />
            <KpiStatCard
              label={t('adminKpis.users.anonymousSharing')}
              value={data.userSegments.anonymousSharing}
              tooltip={t('adminKpis.users.anonymousSharingTooltip')}
              fraction={share(data.userSegments.anonymousSharing, data.userSegments.totalUsers)}
              onClick={() => navigate({ to: '/admin/anonymous-sharing' })}
            />
          </div>

          <Card className="h-full">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsUserSignupsOverTime')}
              </h3>
              <div className="inline-flex gap-1">
                <Button
                  type="button"
                  variant={signupsRange === 'last7' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setSignupsRange('last7')}
                >
                  {t('admin.statisticsLast7Days')}
                </Button>
                <Button
                  type="button"
                  variant={signupsRange === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setSignupsRange('all')}
                >
                  {t('admin.statisticsAllTime')}
                </Button>
              </div>
            </div>
            <div
              ref={signupsRef}
              className="w-full h-80"
            />
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="conversions" forceMount className="mt-4 space-y-6 data-[state=inactive]:hidden">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <KpiStatCard emphasis label={t('adminKpis.conversions.candidatesTotal')} value={data.conversionStats.candidatesTotal} tooltip={t('adminKpis.conversions.candidatesTotalTooltip')} fraction={1} />
            <KpiStatCard label={t('adminKpis.conversions.converted')} value={data.conversionStats.converted} tooltip={t('adminKpis.conversions.convertedTooltip')} fraction={share(data.conversionStats.converted, data.conversionStats.candidatesTotal)} />
            <KpiStatCard label={t('adminKpis.conversions.notConvertedFinished')} value={data.conversionStats.notConvertedFinished} tooltip={t('adminKpis.conversions.notConvertedFinishedTooltip')} fraction={share(data.conversionStats.notConvertedFinished, data.conversionStats.candidatesTotal)} />
            <KpiStatCard
              label={t('adminKpis.conversions.ctaRate')}
              value={formatPct(data.conversionStats.ctaConversionRate)}
              tooltip={t('adminKpis.conversions.ctaRateTooltip')}
              sublabel={t('adminKpis.conversions.ctaRateSublabel')}
              fraction={data.conversionStats.ctaConversionRate}
            />
            <KpiStatCard label={t('adminKpis.conversions.pending')} value={data.conversionStats.pending} tooltip={t('adminKpis.conversions.pendingTooltip')} fraction={share(data.conversionStats.pending, data.conversionStats.candidatesTotal)} />
            <KpiStatCard
              label={t('adminKpis.conversions.rate')}
              value={formatPct(data.conversionStats.conversionRate)}
              tooltip={t('adminKpis.conversions.rateTooltip')}
              sublabel={t('adminKpis.conversions.rateSublabel')}
              fraction={data.conversionStats.conversionRate}
            />
          </div>

          <Card className="h-full">
            <CardContent className="pt-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">
                {t('adminKpis.conversions.funnelTitle')}
              </h3>
              <div ref={conversionFunnelRef} className="w-full h-80" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagement" forceMount className="mt-4 data-[state=inactive]:hidden">
        <Card className="h-full">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsChallengesOverTime')}
              </h3>
              <div className="flex flex-col items-end gap-1">
                <div className="inline-flex gap-1">
                  <Button
                    type="button"
                    variant={challengesMode === 'daily' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setChallengesMode('daily')}
                  >
                    {t('admin.statisticsDaily')}
                  </Button>
                  <Button
                    type="button"
                    variant={challengesMode === 'weekly' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setChallengesMode('weekly')}
                  >
                    {t('admin.statisticsWeekly')}
                  </Button>
                </div>
                {challengesMode === 'daily' && (
                  <div className="inline-flex gap-1">
                    <Button
                      type="button"
                      variant={challengesRange === 'last7' ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setChallengesRange('last7')}
                    >
                      {t('admin.statisticsLast7Days')}
                    </Button>
                    <Button
                      type="button"
                      variant={challengesRange === 'all' ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setChallengesRange('all')}
                    >
                      {t('admin.statisticsAllTime')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div
              ref={challengesRef}
              className="w-full h-80"
            />
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="tasks" forceMount className="mt-4 space-y-6 data-[state=inactive]:hidden">
        <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsTasksOverviewCount')}
              </h3>
              <div className="inline-flex gap-1">
                <Button
                  type="button"
                  variant={tasksRange === 'top5' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('top5')}
                >
                  {t('admin.statisticsTop5')}
                </Button>
                <Button
                  type="button"
                  variant={tasksRange === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('all')}
                >
                  {t('admin.statisticsAll')}
                </Button>
              </div>
            </div>
            <div
              ref={tasksCountRef}
              className="w-full h-96"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('admin.statisticsTasksOverviewGrade')}
              </h3>
              <div className="inline-flex gap-1">
                <Button
                  type="button"
                  variant={tasksRange === 'top5' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('top5')}
                >
                  {t('admin.statisticsTop5')}
                </Button>
                <Button
                  type="button"
                  variant={tasksRange === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setTasksRange('all')}
                >
                  {t('admin.statisticsAll')}
                </Button>
              </div>
            </div>
            <div
              ref={tasksGradeRef}
              className="w-full h-96"
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-start justify-between gap-3 mb-4 sm:flex-row sm:items-center">
            <h3 className="text-lg font-semibold text-foreground">
              {t('admin.statisticsGradeDistribution')}
            </h3>
            <TaskDataInput
              singlePick
              selected={gradeDistributionTaskIds}
              onChange={setGradeDistributionTaskIds}
              className="w-full sm:w-72"
              labels={{ placeholder: t('admin.statisticsGradeDistributionAllTasks') }}
            />
          </div>
          <div
            ref={gradeDistributionRef}
            className="w-full h-96"
          />
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

